import {
  FilterOptionDto,
  SMSMessageDto,
} from '@act/shared/data-transfer-objects';
import { Fragment, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  getSMSFilters,
  getSMSMessages,
  smsMessagesAppSelectors,
} from 'redux/slices/smsMessagesSlice';
import { useAppDispatch } from 'redux/store-hooks';
import StandardTable, {
  DEFAULT_ROWS_PER_PAGE_OPTIONS as rowsPerPageOptions,
} from 'toolkit/tables/standard-table';
import { HeaderCellConfig } from 'toolkit/tables/types/header.types';
import { MenuItemConfig } from 'toolkit/tables/types/menu.types';
import { PaginationBounds } from 'toolkit/tables/types/table.types';
import { PaginatedFilterConfig } from 'utilities/filters/filters.type';
import useConfirmationAlertDialog from '../../dialog-forms/hooks/use-confirmation-alert-dialog';
import useAssignPhoneNumberDialog from '../../form-dialog/hooks/use-assign-phone-number-dialog';
import useAssignTextMessageDialog from '../../form-dialog/hooks/use-assign-text-message-dialog';
import FilterDialog from '../../member-index/filter-dialog';
import TextFilters from './text-filters';
import TextRow from './text-row';

export type TextFiltersForm = {
  [key: string]: FilterOptionDto[];
} & { onlyUnassignedMessages: boolean };

const TextsIndex = ({ active }: { active: boolean }) => {
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const openAssignTextMessageDialog = useAssignTextMessageDialog();
  const openAssignPhoneNumberDialog = useAssignPhoneNumberDialog();

  const [bounds, setBounds] = useState<PaginationBounds>({
    pageSize: rowsPerPageOptions[0],
    pageStart: 0,
  });

  const smsMessages = smsMessagesAppSelectors.useSelectSMSMessagesPage();
  const totalRecords = smsMessagesAppSelectors.useSelectTotalCount();
  const isLoading: boolean =
    smsMessagesAppSelectors.useSelectSMSMessagesSliceStatus() === 'loading';

  const dispatch = useAppDispatch();

  const smsFilterLoadingStatus =
    smsMessagesAppSelectors.useSelectSMSFilterLoadingStatus();
  const filters = smsMessagesAppSelectors.useSelectSMSFilters()?.filters;

  useEffect(() => {
    if (!active) {
      return;
    }
    async function getFilters() {
      await dispatch(getSMSFilters({}));
    }

    if (!filters && smsFilterLoadingStatus !== 'loading') {
      getFilters();
    }
  }, [filters, smsFilterLoadingStatus, active]);

  const useFormState = useForm<PaginatedFilterConfig>({
    defaultValues: getDefaults(),
  });

  const { control, reset, getValues, setValue, watch } = useFormState;

  const onlyUnassignedMessages = watch('onlyUnassignedMessages');

  function countActiveFilters() {
    const count = Object.entries(getValues())
      .filter(
        ([k]) =>
          ![
            'searchTerm',
            'searchTermType',
            'orderBy',
            'orderDirection',
          ].includes(k)
      )
      .reduce((acc, [key, val]) => {
        if (
          !(val as FilterOptionDto[])?.length &&
          !(key === 'onlyUnassignedMessages' && val === true)
        ) {
          return acc;
        }

        return ++acc;
      }, 0);

    return count;
  }

  async function handleReset() {
    reset(getDefaults(true));
    window.localStorage.removeItem('textsFilterConfig');
    await handleFiltering();
  }

  useEffect(() => {
    void handleFiltering();
  }, [onlyUnassignedMessages]);

  async function handleFiltering(resetPage?: boolean): Promise<void> {
    const filterValues = getValues();
    const filterConfig = { 'page-start': 0 };

    if (resetPage) {
      filterValues['page-start'] = 0;
    }

    Object.entries(getValues())
      .filter(
        ([k, v]) =>
          ![
            'guideId',
            'onlyUnassignedMessages',
            'showOnlyMyTexts',
            'page-start',
            'page-size',
            'orderBy',
            'orderDirection',
            'directionType',
            'searchTermType',
            'searchTerm',
            'subjectIds',
            'lastDaysFromNow',
            'betweenDays',
          ].includes(k) && (v as FilterOptionDto[])?.length
      )
      .forEach(([key, val]) => {
        filterConfig[key] = val as FilterOptionDto[];
      });

    filterConfig['onlyUnassignedMessages'] =
      filterValues['onlyUnassignedMessages'];
    filterConfig['showOnlyMyTexts'] = filterValues['showOnlyMyTexts'];
    filterConfig['page-start'] = filterValues['page-start'];
    filterConfig['page-size'] = filterValues['page-size'];
    filterConfig['includeCount'] = true;
    filterConfig['orderBy'] = filterValues['orderBy'];
    filterConfig['orderDirection'] = filterValues['orderDirection'];
    filterConfig['directionType'] = filterValues['directionType'];
    filterConfig['searchTermType'] = filterValues['searchTermType'];
    filterConfig['lastDaysFromNow'] = filterValues['lastDaysFromNow'];
    filterConfig['subjectIds'] = filterValues['subjectIds']
      ? [filterValues['subjectIds']]
      : [];
    filterConfig['searchTerm'] = filterValues['searchTerm'];

    window.localStorage.setItem(
      'textsFilterConfig',
      JSON.stringify({ ...filterValues })
    );

    setFilterConfig({ ...filterConfig } as PaginatedFilterConfig);

    setOpen(false);
  }

  const [open, setOpen] = useState<boolean>(false);
  const [filterConfig, setFilterConfig] = useState<PaginatedFilterConfig>(null);
  const [loadedFilterConfig, setLoadedFilterConfig] =
    useState<PaginatedFilterConfig>(null);

  useEffect(() => {
    if (!filterConfig || !active) {
      return;
    }

    filterSMSMessages(filterConfig);
  }, [filterConfig, active]);

  function getConfigFromStore() {
    const existingConfig = window.localStorage.getItem('textsFilterConfig');
    if (existingConfig) {
      const parsedConfig = JSON.parse(existingConfig);
      if (parsedConfig.lastDaysFromNow === undefined) {
        parsedConfig.lastDaysFromNow = 30;
      }
      return parsedConfig;
    }

    return null;
  }

  function getDefaults(reset?: boolean): {
    [key: string]: unknown[] | boolean | number | string;
  } {
    const existingConfig = getConfigFromStore();

    if (existingConfig && !reset) {
      return existingConfig;
    }

    const pageSize = existingConfig?.['page-size'] || rowsPerPageOptions[0];
    const pageStart = 0;

    const defaults = {
      searchTerm: '',
      showOnlyMyTexts: false,
      includeCount: true,
      searchTermType: null,
      'page-start': pageStart,
      'page-size': pageSize,
      subjectIds: null,
      orderBy: null,
      directionType: null,
      orderDirection: 'DESC',
      onlyUnassignedMessages: false,
      lastDaysFromNow: 30,
      guideIds: [],
    };
    filters?.forEach(f => (defaults[f.key] = []));
    return defaults;
  }

  useEffect(() => {
    handleFiltering();
  }, []);

  async function filterSMSMessages(config: PaginatedFilterConfig) {
    // CALCULATING PAGE START; SHOULD DISCUSS MOVING TO BACKEND
    if (loadedFilterConfig === config) {
      return;
    }

    setLoadedFilterConfig(config);
    dispatch(
      getSMSMessages({
        params: {
          ...config,
          ...(config.guideIds && {
            guideIds: config.guideIds as string[],
          }),
          'page-start': config['page-size'] * config['page-start'],
        },
      })
    );
  }

  async function handleFetchSmsMessages({ orderBy, pageSize, pageStart }) {
    async function fetchSmsMessages() {
      setValue('orderBy', orderBy);
      setValue('orderDirection', 'DESC');
      setValue('page-size', pageSize);
      setValue('page-start', pageStart);
    }

    await fetchSmsMessages();

    handleFiltering();
  }

  const headerCells: HeaderCellConfig<keyof SMSMessageDto>[] = [
    {
      key: 'from',
      label: 'From',
      width: 20,
    },
    {
      key: 'to',
      label: 'To',
      width: 20,
    },
    {
      key: 'createdAt',
      label: 'Date and time',
      width: 20,
    },
    {
      key: 'text',
      label: 'Message',
      width: 35,
    },
    {
      key: 'menu',
      width: 5,
    },
  ];

  const menuItems: MenuItemConfig<SMSMessageDto>[] = [
    {
      display: 'View text message',
      callback: (data: SMSMessageDto) =>
        openConfirmationAlertDialog({
          title: 'Message',
          showClose: true,
          hideIcon: true,
          hideActions: true,
          message: data.text,
        }),
    },
    {
      display: 'Assign text message (one time)',
      callback: (data: SMSMessageDto) => {
        openAssignTextMessageDialog({
          sms: data,
        });
      },
      hidden: (data: SMSMessageDto) => {
        return !!data?.message;
      },
    },
    {
      display: 'Assign phone number',
      callback: (data: SMSMessageDto) => {
        const phoneNumber = data.external ? data.from : data.to;
        openAssignPhoneNumberDialog({ phoneNumber, sms: data });
      },
    },
  ];

  return (
    <Fragment>
      <TextFilters
        control={control}
        activeFilterCount={countActiveFilters()}
        setValue={setValue}
        getValues={getValues}
        handleReset={handleReset}
        handleOpenFilters={() => setOpen(true)}
        handleFiltering={() => handleFiltering(true)}
        currentFilterConfig={filterConfig}
      />
      <StandardTable
        isLoading={isLoading}
        data={smsMessages}
        dataRowElement={TextRow}
        bounds={bounds}
        headerCells={headerCells}
        menuItems={menuItems}
        totalRecords={totalRecords || 0}
        dispatchAction={handleFetchSmsMessages}
        setBounds={setBounds}
        ariaLabel="Texts"
      />

      <FilterDialog
        open={open}
        control={control}
        filters={filters}
        handleReset={handleReset}
        handleClose={() => setOpen(false)}
        handleFiltering={() => handleFiltering(true)}
      />
    </Fragment>
  );
};

export default TextsIndex;
